import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container, Typography, Box, Button, Grid, Card, CardContent, CardActions,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, Chip, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Tabs, Tab, IconButton, Snackbar, InputAdornment, Divider, Tooltip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import LinkIcon from '@mui/icons-material/Link';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import AddIcon from '@mui/icons-material/Add';
import QRCode from 'react-qr-code';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ReceiptIcon from '@mui/icons-material/Receipt';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import api from '../../utils/axiosConfig';
import { useAuth } from '../Login/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';

const translations = {
  TH: {
    title: 'จัดการข้อมูลธุรกิจ',
    myCoupons: 'คูปองของฉัน',
    redemptions: 'ประวัติการใช้',
    createCoupon: 'สร้างคูปอง',
    editCoupon: 'แก้ไขคูปอง',
    titleTh: 'ชื่อคูปอง (ไทย)',
    titleEn: 'ชื่อคูปอง (อังกฤษ)',
    discountType: 'ประเภทส่วนลด',
    percentage: 'เปอร์เซ็นต์',
    fixed: 'จำนวนเงินคงที่',
    discountValue: 'มูลค่าส่วนลด',
    discountValuePercent: 'เปอร์เซ็นต์ส่วนลด',
    maxDiscount: 'ส่วนลดสูงสุด (บาท)',
    validFrom: 'วันเริ่มต้น',
    validUntil: 'วันหมดอายุ',
    maxUses: 'จำนวนสิทธิ์สูงสุด',
    termsTh: 'เงื่อนไข (ไทย)',
    save: 'บันทึก',
    cancel: 'ยกเลิก',
    delete: 'ลบ',
    status: 'สถานะ',
    active: 'ใช้งานได้',
    depleted: 'หมดสิทธิ์',
    uses: 'ใช้ไปแล้ว',
    noData: 'ไม่มีข้อมูล',
    loading: 'กำลังโหลด...',
    successCreate: 'สร้างคูปองสำเร็จ!',
    successUpdate: 'อัปเดตคูปองสำเร็จ!',
    successDelete: 'ลบคูปองสำเร็จ!',
    errorLoad: 'โหลดข้อมูลล้มเหลว',
    noBusiness: 'ไม่พบข้อมูลธุรกิจ กรุณาติดต่อผู้ดูแลระบบ',
    discount: 'ส่วนลด',
    customer: 'ลูกค้า',
    discountGiven: 'ส่วนลดที่ให้',
    date: 'วันที่'
  },
  EN: {
    title: 'Business Management',
    myCoupons: 'My Coupons',
    redemptions: 'Redemption History',
    createCoupon: 'Create Coupon',
    editCoupon: 'Edit Coupon',
    titleTh: 'Coupon Title (Thai)',
    titleEn: 'Coupon Title (English)',
    discountType: 'Discount Type',
    percentage: 'Percentage',
    fixed: 'Fixed Amount',
    discountValue: 'Discount Value',
    discountValuePercent: 'Percentage Discount',
    maxDiscount: 'Max Discount (THB)',
    validFrom: 'Valid From',
    validUntil: 'Valid Until',
    maxUses: 'Max Uses',
    termsTh: 'Terms (Thai)',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    status: 'Status',
    active: 'Active',
    depleted: 'Depleted',
    uses: 'Uses',
    noData: 'No data',
    loading: 'Loading...',
    successCreate: 'Coupon created!',
    successUpdate: 'Coupon updated!',
    successDelete: 'Coupon deleted!',
    errorLoad: 'Failed to load data',
    noBusiness: 'No business record found. Please contact admin.',
    discount: 'Discount',
    customer: 'Customer',
    discountGiven: 'Discount Given',
    date: 'Date'
  },
  ZH: {
    title: '商家优惠券管理',
    myCoupons: '我的优惠券',
    redemptions: '兑换记录',
    createCoupon: '创建优惠券',
    editCoupon: '编辑优惠券',
    titleTh: '优惠券标题（泰语）',
    titleEn: '优惠券标题（英语）',
    discountType: '折扣类型',
    percentage: '百分比',
    fixed: '固定金额',
    discountValue: '折扣值',
    discountValuePercent: '折扣百分比',
    maxDiscount: '最大折扣（泰铢）',
    validFrom: '开始日期',
    validUntil: '截止日期',
    maxUses: '最大使用次数',
    termsTh: '条款（泰语）',
    save: '保存',
    cancel: '取消',
    delete: '删除',
    status: '状态',
    active: '有效',
    depleted: '已用完',
    uses: '已使用',
    noData: '暂无数据',
    loading: '加载中...',
    successCreate: '优惠券创建成功！',
    successUpdate: '优惠券更新成功！',
    successDelete: '优惠券删除成功！',
    errorLoad: '加载数据失败',
    noBusiness: '未找到商家记录，请联系管理员。',
    discount: '折扣',
    customer: '客户',
    discountGiven: '已给折扣',
    date: '日期'
  }
};

const getDefaultForm = () => {
  const today = new Date();
  const threeMonths = new Date(today);
  threeMonths.setMonth(threeMonths.getMonth() + 3);
  const toDateStr = (d) => d.toISOString().split('T')[0];
  return {
    business_id: '',
    title: '',
    title_th: '',
    discount_type: 'percentage',
    discount_value: '',
    max_discount_amount: '',
    valid_from: toDateStr(today),
    valid_until: toDateStr(threeMonths),
    max_uses: '1000',
    terms_th: '',
    coupon_code: ''
  };
};

// Cost breakdown info shown to owner when creating a coupon
const CouponCostInfo = ({ discountType, discountValue }) => {
  const v = parseFloat(discountValue);
  if (!v || v <= 0) return null;
  const isPct = discountType === 'percentage';
  const fmt = (n) => {
    const rounded = parseFloat(n.toFixed(2));
    return isPct ? `${rounded}%` : `${rounded} บาท`;
  };
  const rows = [
    { label: 'ส่วนลดที่มอบให้ลูกค้า', value: fmt(v), color: 'text.primary' },
    { label: 'ค่าบริการแพลตฟอร์ม (20% ของส่วนลด)', value: `+ ${fmt(v * 0.2)}`, color: '#1565c0' },
    { label: 'ค่าตอบแทนผู้แนะนำ / นักรีวิว (20% ของส่วนลด)', value: `+ ${fmt(v * 0.2)}`, color: '#2e7d32' },
  ];
  return (
    <Box sx={{ mt: 1.5, p: 2, borderRadius: 2, bgcolor: '#fffbf0', border: '1px solid rgba(243,156,18,0.3)' }}>
      <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 1, color: '#b7791f', letterSpacing: 0.4 }}>
        โครงสร้างต้นทุนต่อการใช้คูปอง 1 ครั้ง
      </Typography>
      {rows.map((r, i) => (
        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.35 }}>
          <Typography variant="caption" color="text.secondary">{r.label}</Typography>
          <Typography variant="caption" fontWeight={600} sx={{ color: r.color }}>{r.value}</Typography>
        </Box>
      ))}
      <Divider sx={{ my: 0.75 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="caption" fontWeight={700} color="warning.dark">ต้นทุนรวมที่ธุรกิจรับผิดชอบ</Typography>
        <Typography variant="caption" fontWeight={700} color="warning.dark">{fmt(v * 1.4)}</Typography>
      </Box>
      <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: 'text.disabled', lineHeight: 1.5 }}>
        ค่าบริการแพลตฟอร์มและค่าตอบแทนผู้แนะนำจะถูกคิดโดยอัตโนมัติเมื่อลูกค้าใช้คูปองจริง
      </Typography>
    </Box>
  );
};

const VenueOwnerDashboard = () => {
  const { language } = useLanguage();
  const lang = language?.toUpperCase() === 'ZH' ? 'ZH' : language?.toUpperCase() === 'EN' ? 'EN' : 'TH';
  const t = translations[lang];
  const { isAuthenticated } = useAuth();

  const [tab, setTab] = useState(0);
  const [business, setBusiness] = useState(null);
  const [myBusinesses, setMyBusinesses] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [form, setForm] = useState(getDefaultForm());
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [nearbyBusinesses, setNearbyBusinesses] = useState([]);
  const [loadingNearby, setLoadingNearby] = useState(false);
  const [claimDialog, setClaimDialog] = useState({ open: false, business: null });
  const [claimDocs, setClaimDocs] = useState([]);
  const [claimUploading, setClaimUploading] = useState(false);
  const [claimSubmitting, setClaimSubmitting] = useState(false);
  const [claimedIds, setClaimedIds] = useState([]);
  const [bizDialogOpen, setBizDialogOpen] = useState(false);
  const [editingBiz, setEditingBiz] = useState(null);
  const [bizForm, setBizForm] = useState({ business_name: '', contact_phone: '', contact_email: '', address: '' });
  const [savingBiz, setSavingBiz] = useState(false);
  const [deletingBizId, setDeletingBizId] = useState(null);
  const canvasRef = useRef(null);
  const [imageGenerated, setImageGenerated] = useState(false);
  const [codeCheckStatus, setCodeCheckStatus] = useState('idle'); // 'idle' | 'checking' | 'available' | 'duplicate'
  const [shopQrBiz, setShopQrBiz] = useState(null); // business whose QR is being shown
  const [showQrWarning, setShowQrWarning] = useState(false);

  const handleGenerateImage = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const W = 320, H = 150;
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext('2d');

    // Background
    ctx.fillStyle = '#f39c12';
    ctx.fillRect(0, 0, W, H);

    // Darker stripe at bottom
    ctx.fillStyle = '#e67e22';
    ctx.fillRect(0, H - 44, W, 44);

    // Dashed border
    ctx.setLineDash([6, 4]);
    ctx.strokeStyle = 'rgba(255,255,255,0.5)';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(7, 7, W - 14, H - 14);
    ctx.setLineDash([]);

    // Coupon code — large, centered, top area
    const code = form.coupon_code || '------';
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 38px "Courier New", monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(code, W / 2, 65);

    // Divider line
    ctx.strokeStyle = 'rgba(255,255,255,0.4)';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 3]);
    ctx.beginPath(); ctx.moveTo(16, 106); ctx.lineTo(W - 16, 106); ctx.stroke();
    ctx.setLineDash([]);

    // English title — bottom strip
    const title = (form.title || 'Coupon').slice(0, 36);
    ctx.font = '500 13px Arial, sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    ctx.fillText(title, W / 2, H - 22);

    setImageGenerated(true);
  }, [form.coupon_code, form.title]);

  useEffect(() => {
    const code = form.coupon_code;
    if (code.length < 6) {
      setCodeCheckStatus('idle');
      return;
    }
    setCodeCheckStatus('checking');
    const timer = setTimeout(async () => {
      try {
        const res = await api.get(`/coupons/check-code/${code}`);
        setCodeCheckStatus(res.data.taken ? 'duplicate' : 'available');
      } catch {
        setCodeCheckStatus('idle');
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [form.coupon_code]);

  const loadBusiness = useCallback(async () => {
    try {
      const res = await api.get('/businesses/my');
      const list = Array.isArray(res.data) ? res.data : (res.data ? [res.data] : []);
      setMyBusinesses(list);
      const first = list[0] || null;
      setBusiness(first);
      return first;
    } catch (err) {
      setError(t.errorLoad);
      return null;
    }
  }, [t.errorLoad]);

  const loadCoupons = useCallback(async (businessId) => {
    try {
      const res = await api.get(`/coupons/business/${businessId}`);
      setCoupons(res.data || []);
    } catch (err) {
      console.error('Failed to load coupons', err);
    }
  }, []);

  const loadRedemptions = useCallback(async (businessId) => {
    try {
      const res = await api.get(`/redemptions/business/${businessId}`);
      setRedemptions(res.data || []);
    } catch (err) {
      console.error('Failed to load redemptions', err);
    }
  }, []);

  const loadNearby = useCallback(async () => {
    setLoadingNearby(true);
    try {
      const res = await api.get('/businesses/nearby?radius=10');
      setNearbyBusinesses(res.data.businesses || []);
    } catch (err) {
      console.error('Failed to load nearby businesses', err);
    } finally {
      setLoadingNearby(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    setLoading(true);
    loadBusiness().then(biz => {
      if (biz?.id) {
        return Promise.all([loadCoupons(biz.id), loadRedemptions(biz.id)]);
      }
    }).finally(() => setLoading(false));
    loadNearby();
  }, [isAuthenticated, loadBusiness, loadCoupons, loadRedemptions, loadNearby]);

  const handleOpenCreate = () => {
    setEditingCoupon(null);
    setForm({ ...getDefaultForm(), business_id: business?.id || '' });
    setImageGenerated(false);
    setCodeCheckStatus('idle');
    setDialogOpen(true);
  };

  const handleOpenEdit = (coupon) => {
    setEditingCoupon(coupon);
    setImageGenerated(false);
    setCodeCheckStatus('idle');
    setForm({
      business_id: coupon.business_id || business?.id || '',
      title: coupon.title_en || coupon.title || '',
      title_th: coupon.title_th || '',
      discount_type: coupon.discount_type || 'percentage',
      discount_value: coupon.discount_value || '',
      max_discount_amount: coupon.max_discount_amount || '',
      valid_from: coupon.valid_from ? coupon.valid_from.split('T')[0] : '',
      valid_until: coupon.valid_until ? coupon.valid_until.split('T')[0] : '',
      max_uses: coupon.max_uses || '',
      terms_th: coupon.terms_th || '',
      coupon_code: coupon.coupon_code || ''
    });
    setDialogOpen(true);
  };

  const handleOpenEditBiz = (biz) => {
    setEditingBiz(biz);
    setBizForm({ business_name: biz.business_name || '', contact_phone: biz.contact_phone || '', contact_email: biz.contact_email || '', address: biz.address || '' });
    setBizDialogOpen(true);
  };

  const handleSaveBiz = async () => {
    if (!editingBiz) return;
    setSavingBiz(true);
    try {
      const res = await api.put(`/businesses/${editingBiz.id}`, bizForm);
      setMyBusinesses(prev => prev.map(b => b.id === editingBiz.id ? { ...b, ...res.data } : b));
      if (business?.id === editingBiz.id) setBusiness(prev => ({ ...prev, ...res.data }));
      setBizDialogOpen(false);
      setSnackbar({ open: true, message: 'อัปเดตธุรกิจสำเร็จ', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Error', severity: 'error' });
    } finally {
      setSavingBiz(false);
    }
  };

  const handleDeleteBiz = async (bizId) => {
    if (!window.confirm('ยืนยันการลบธุรกิจนี้? การกระทำนี้ไม่สามารถย้อนกลับได้')) return;
    setDeletingBizId(bizId);
    try {
      await api.delete(`/businesses/${bizId}`);
      const updated = myBusinesses.filter(b => b.id !== bizId);
      setMyBusinesses(updated);
      if (business?.id === bizId) setBusiness(updated[0] || null);
      setSnackbar({ open: true, message: 'ลบธุรกิจสำเร็จ', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Error', severity: 'error' });
    } finally {
      setDeletingBizId(null);
    }
  };

  const handleSave = async () => {
    if (!form.business_id || !form.title || !form.discount_value || !form.valid_from || !form.valid_until) return;
    setSaving(true);
    const payload = { ...form };
    const targetBusinessId = form.business_id;
    try {
      if (editingCoupon) {
        await api.put(`/coupons/${editingCoupon.id}`, payload);
        setSnackbar({ open: true, message: t.successUpdate, severity: 'success' });
      } else {
        await api.post('/coupons', payload);
        setSnackbar({ open: true, message: t.successCreate, severity: 'success' });
        setShowQrWarning(true);
      }
      setDialogOpen(false);
      await loadCoupons(targetBusinessId);
    } catch (err) {
      if (err.response?.status === 409) {
        setSnackbar({ open: true, message: 'รหัสคูปองซ้ำ กรุณากดสร้างรหัสใหม่แล้วบันทึกอีกครั้ง', severity: 'warning' });
      } else {
        setSnackbar({ open: true, message: err.response?.data?.error || 'Error', severity: 'error' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (couponId) => {
    if (!window.confirm('ยืนยันการยกเลิกคูปองนี้? คูปองจะยังคงอยู่ในระบบแต่ผู้ใช้จะไม่สามารถเก็บได้อีก')) return;
    try {
      await api.delete(`/coupons/${couponId}`);
      setSnackbar({ open: true, message: t.successDelete, severity: 'success' });
      await loadCoupons(business.id);
    } catch (err) {
      setSnackbar({ open: true, message: 'Error deleting coupon', severity: 'error' });
    }
  };

  const handleClaimFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setClaimUploading(true);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('documents', f));
      const res = await api.post('/users/upload-role-document', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setClaimDocs(prev => [...prev, ...(res.data.urls || [])]);
    } catch {
      setSnackbar({ open: true, message: 'อัปโหลดไฟล์ไม่สำเร็จ', severity: 'error' });
    } finally {
      setClaimUploading(false);
      e.target.value = '';
    }
  };

  const handleSubmitClaim = async () => {
    if (!claimDialog.business || claimDocs.length === 0) return;
    setClaimSubmitting(true);
    try {
      await api.post(`/businesses/${claimDialog.business.id}/claim`, { document_urls: claimDocs });
      setClaimedIds(prev => [...prev, claimDialog.business.id]);
      setClaimDialog({ open: false, business: null });
      setClaimDocs([]);
      setSnackbar({ open: true, message: 'ส่งคำขอยืนยันสิทธิ์สำเร็จ รอการอนุมัติ', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error === 'Claim already pending' ? 'คุณได้ส่งคำขอนี้แล้ว' : 'เกิดข้อผิดพลาด', severity: 'error' });
    } finally {
      setClaimSubmitting(false);
    }
  };

  const getStatusChip = (coupon) => {
    if (coupon.status === 'active') return <Chip label={t.active} color="success" size="small" />;
    if (coupon.status === 'cancelled') return <Chip label="ยกเลิกแล้ว" color="default" size="small" />;
    return <Chip label={t.depleted} color="warning" size="small" />;
  };

  const formatDiscount = (coupon) => {
    if (coupon.discount_type === 'percentage') return `${coupon.discount_value}%`;
    return `${coupon.discount_value} THB`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!business && myBusinesses.length === 0) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">{t.noBusiness}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={() => window.history.back()}
            sx={{ color: 'text.primary' }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" fontWeight={700}>
            {t.title}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="warning"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
        >
          {t.createCoupon}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}


      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab icon={<LocalOfferIcon />} label={`${t.myCoupons} (${coupons.length})`} iconPosition="start" />
        <Tab icon={<ReceiptIcon />} label={`${t.redemptions} (${redemptions.length})`} iconPosition="start" />
        <Tab icon={<BusinessIcon />} label={`ธุรกิจของฉัน (${myBusinesses.length})`} iconPosition="start" />
        <Tab icon={<LocationOnIcon />} label={`สถานที่ใกล้เคียง (${nearbyBusinesses.length})`} iconPosition="start" />
      </Tabs>

      {/* Coupons Tab */}
      {tab === 0 && (
        <>
          {showQrWarning && (
            <Alert
              severity="warning"
              icon={<QrCode2Icon />}
              onClose={() => setShowQrWarning(false)}
              sx={{ mb: 2, alignItems: 'center' }}
              action={
                <Button
                  size="small"
                  color="warning"
                  variant="contained"
                  startIcon={<QrCode2Icon />}
                  onClick={() => { setTab(2); setShowQrWarning(false); }}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  ไปที่ธุรกิจของฉัน
                </Button>
              }
            >
              <Typography variant="body2" fontWeight={600}>สร้างคูปองสำเร็จแล้ว!</Typography>
              <Typography variant="caption">
                อย่าลืมไปพิมพ์ <strong>QR Code ร้านค้า</strong> ในแท็บ "ธุรกิจของฉัน" แล้วติดไว้ที่หน้าร้าน
                เพื่อให้ลูกค้าสแกนนำคูปองไปใช้ได้จริง
              </Typography>
            </Alert>
          )}
          {coupons.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">{t.noData}</Typography>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {coupons.map(coupon => (
                <Grid item xs={12} sm={6} md={4} key={coupon.id}>
                  <Card variant="outlined" sx={{
                    borderColor: coupon.status === 'active' ? '#f39c12' : '#ccc',
                    borderWidth: 2,
                    opacity: coupon.status === 'cancelled' ? 0.55 : 1,
                    bgcolor: coupon.status === 'cancelled' ? '#f5f5f5' : 'background.paper'
                  }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="h6" color={coupon.status === 'cancelled' ? 'text.secondary' : 'warning.main'} fontWeight={700}>
                          {formatDiscount(coupon)}
                        </Typography>
                        {getStatusChip(coupon)}
                      </Box>
                      <Typography variant="body2" fontWeight={600} mb={0.5}>
                        {coupon.title_th || coupon.title || 'Coupon'}
                      </Typography>
                      {coupon.coupon_code && (
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', letterSpacing: 2, bgcolor: '#f0f0f0', px: 0.8, py: 0.2, borderRadius: 1, display: 'inline-block', mb: 0.5 }}>
                          {coupon.coupon_code}
                        </Typography>
                      )}
                      {coupon.valid_until && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          หมดอายุ: {new Date(coupon.valid_until).toLocaleDateString('th-TH')}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          เก็บแล้ว: {coupon.collections_count || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ใช้แล้ว: {coupon.uses_count || 0}{coupon.max_uses ? ` / ${coupon.max_uses}` : ''}
                        </Typography>
                      </Box>
                    </CardContent>
                    <CardActions sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                      <IconButton size="small" color="primary" onClick={() => handleOpenEdit(coupon)} disabled={coupon.status === 'cancelled'}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      {coupon.status !== 'cancelled' && (
                        <IconButton size="small" color="error" onClick={() => handleDelete(coupon.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                      {coupon.coupon_code && (
                        <Tooltip title="คัดลอกลิงก์แชร์คูปอง">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => {
                              const payload = { c: coupon.coupon_code, b: coupon.business_id };
                              const token = btoa(JSON.stringify(payload))
                                .replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
                              const base = process.env.REACT_APP_COUPON_BASE_URL
                                || `${window.location.origin}/c`;
                              navigator.clipboard.writeText(`${base}/${token}`);
                              setSnackbar({ open: true, message: 'คัดลอกลิงก์คูปองแล้ว!', severity: 'success' });
                            }}
                          >
                            <LinkIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      {/* Redemptions Tab */}
      {tab === 1 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t.customer}</TableCell>
                <TableCell>{t.discount}</TableCell>
                <TableCell>{t.discountGiven}</TableCell>
                <TableCell>{t.date}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {redemptions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">{t.noData}</TableCell>
                </TableRow>
              ) : (
                redemptions.map(r => (
                  <TableRow key={r.id}>
                    <TableCell>{r.customer_name || '-'}</TableCell>
                    <TableCell>{r.title_th || r.title_en || '-'}</TableCell>
                    <TableCell>{r.discount_given ? `${r.discount_given} THB` : '-'}</TableCell>
                    <TableCell>
                      {r.confirmed_at ? new Date(r.confirmed_at).toLocaleDateString() : '-'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* My Businesses Tab */}
      {tab === 2 && (
        <Box>
          {myBusinesses.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">ไม่มีข้อมูลธุรกิจ</Typography>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {myBusinesses.map(biz => (
                <Grid item xs={12} sm={6} md={4} key={biz.id}>
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <BusinessIcon color="warning" />
                        <Typography variant="h6" fontWeight={700} noWrap>
                          {biz.business_name || biz.venue_name || `Business #${biz.id}`}
                        </Typography>
                      </Box>
                      {biz.venue_name && biz.venue_name !== biz.business_name && (
                        <Typography variant="body2" color="text.secondary" mb={0.5}>{biz.venue_name}</Typography>
                      )}
                      {biz.contact_phone && (
                        <Typography variant="body2" color="text.secondary">📞 {biz.contact_phone}</Typography>
                      )}
                      {biz.contact_email && (
                        <Typography variant="body2" color="text.secondary">✉️ {biz.contact_email}</Typography>
                      )}
                      {biz.address && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>📍 {biz.address}</Typography>
                      )}
                      <Chip
                        label={biz.role_request_status === 'approved' ? 'ยืนยันแล้ว' : biz.role_request_status === 'rejected' ? 'ถูกปฏิเสธ' : 'รอการยืนยัน'}
                        color={biz.role_request_status === 'approved' ? 'success' : biz.role_request_status === 'rejected' ? 'error' : 'default'}
                        size="small"
                        sx={{ mt: 1.5 }}
                      />
                    </CardContent>
                    <CardActions sx={{ px: 2, pb: 2, gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => handleOpenEditBiz(biz)}
                      >
                        แก้ไข
                      </Button>
                      {biz.shop_token && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          startIcon={<QrCode2Icon />}
                          onClick={() => setShopQrBiz(biz)}
                        >
                          QR ร้านค้า
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={deletingBizId === biz.id ? <CircularProgress size={14} /> : <DeleteIcon />}
                        onClick={() => handleDeleteBiz(biz.id)}
                        disabled={deletingBizId === biz.id}
                      >
                        ลบ
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* Nearby Businesses Tab */}
      {tab === 3 && (
        <Box>
          {loadingNearby ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : nearbyBusinesses.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">ไม่พบสถานที่ใกล้เคียงในรัศมี 10 กม.</Typography>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {nearbyBusinesses.map(biz => (
                <Grid item xs={12} sm={6} md={4} key={biz.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" fontWeight={700} mb={0.5}>
                        {biz.venue_name || biz.business_name || `Business #${biz.id}`}
                      </Typography>
                      {biz.address && (
                        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                          <LocationOnIcon sx={{ fontSize: 12, mr: 0.5 }} />
                          {biz.address}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" display="block">
                        ห่าง {biz.distance_km?.toFixed(2)} กม. • เจ้าของ: {biz.owner_name || '-'}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        disabled={claimedIds.includes(biz.id)}
                        onClick={() => { setClaimDialog({ open: true, business: biz }); setClaimDocs([]); }}
                      >
                        {claimedIds.includes(biz.id) ? 'ส่งคำขอแล้ว' : 'ขอยืนยันสิทธิ์'}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ pb: 1, fontWeight: 700, fontSize: '1.2rem' }}>
          {editingCoupon ? t.editCoupon : t.createCoupon}
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2, pb: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

            {/* Business */}
            <Box>
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.75, display: 'block' }}>ธุรกิจ</Typography>
              <FormControl fullWidth required disabled={!!editingCoupon} sx={{ '& .MuiInputBase-root': { height: 48 } }}>
                <InputLabel>เลือกธุรกิจ</InputLabel>
                <Select value={form.business_id} label="เลือกธุรกิจ" onChange={e => setForm(f => ({ ...f, business_id: e.target.value }))}>
                  {myBusinesses.map(biz => (
                    <MenuItem key={biz.id} value={biz.id}>{biz.business_name || biz.venue_name || `Business #${biz.id}`}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Divider />

            {/* Coupon Name */}
            <Box>
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.75, display: 'block' }}>ชื่อคูปอง</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <TextField label={t.titleEn} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} fullWidth required disabled={!!editingCoupon} sx={{ '& .MuiInputBase-root': { height: 48 } }} />
                <TextField label={t.titleTh} value={form.title_th} onChange={e => setForm(f => ({ ...f, title_th: e.target.value }))} fullWidth disabled={!!editingCoupon} sx={{ '& .MuiInputBase-root': { height: 48 } }} />
              </Box>
            </Box>

            <Divider />

            {/* Discount */}
            <Box>
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.75, display: 'block' }}>รายละเอียดส่วนลด</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <FormControl fullWidth disabled={!!editingCoupon} sx={{ '& .MuiInputBase-root': { height: 48 } }}>
                      <InputLabel>{t.discountType}</InputLabel>
                      <Select value={form.discount_type} label={t.discountType} onChange={e => setForm(f => ({ ...f, discount_type: e.target.value }))}>
                        <MenuItem value="percentage">{t.percentage}</MenuItem>
                        <MenuItem value="fixed">{t.fixed}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label={form.discount_type === 'percentage' ? t.discountValuePercent : t.discountValue}
                      value={form.discount_value} onChange={e => setForm(f => ({ ...f, discount_value: e.target.value }))}
                      fullWidth type="number" required disabled={!!editingCoupon}
                      sx={{ '& .MuiInputBase-root': { height: 48 } }}
                      InputProps={{ endAdornment: <InputAdornment position="end">{form.discount_type === 'percentage' ? '%' : 'บาท'}</InputAdornment> }}
                    />
                  </Grid>
                </Grid>
                {form.discount_type === 'percentage' && (
                  <TextField label={t.maxDiscount} value={form.max_discount_amount} onChange={e => setForm(f => ({ ...f, max_discount_amount: e.target.value }))} fullWidth type="number" disabled={!!editingCoupon} sx={{ '& .MuiInputBase-root': { height: 48 } }} />
                )}
                {!editingCoupon && (
                  <CouponCostInfo discountType={form.discount_type} discountValue={form.discount_value} />
                )}
              </Box>
            </Box>

            <Divider />

            {/* Validity — always editable */}
            <Box>
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.75, display: 'block' }}>ระยะเวลาและสิทธิ์</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <TextField label={t.validFrom} value={form.valid_from} onChange={e => setForm(f => ({ ...f, valid_from: e.target.value }))} fullWidth type="date" InputLabelProps={{ shrink: true }} required sx={{ '& .MuiInputBase-root': { height: 48 } }} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField label={t.validUntil} value={form.valid_until} onChange={e => setForm(f => ({ ...f, valid_until: e.target.value }))} fullWidth type="date" InputLabelProps={{ shrink: true }} required sx={{ '& .MuiInputBase-root': { height: 48 } }} />
                  </Grid>
                </Grid>
                <TextField label={t.maxUses} value={form.max_uses} onChange={e => setForm(f => ({ ...f, max_uses: e.target.value }))} fullWidth type="number" sx={{ '& .MuiInputBase-root': { height: 48 } }} />
              </Box>
            </Box>

            {/* Coupon Code + Image — hidden in edit mode */}
            {!editingCoupon && (
              <>
                <Divider />
                <Box>
                  <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.75, display: 'block' }}>รหัสคูปอง</Typography>
                  <TextField
                    label="รหัสคูปอง (6 ตัวอักษร)"
                    value={form.coupon_code}
                    onChange={e => {
                      setForm(f => ({ ...f, coupon_code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6) }));
                      setImageGenerated(false);
                    }}
                    fullWidth
                    inputProps={{ maxLength: 6, style: { fontFamily: 'monospace', letterSpacing: 4, fontSize: '1.1rem' } }}
                    InputProps={{
                      endAdornment: form.coupon_code.length === 6 ? (
                        <InputAdornment position="end">
                          {codeCheckStatus === 'checking' && <CircularProgress size={18} />}
                          {codeCheckStatus === 'available' && (
                            <Tooltip title="รหัสนี้ใช้ได้">
                              <CheckCircleIcon color="success" fontSize="small" />
                            </Tooltip>
                          )}
                          {codeCheckStatus === 'duplicate' && (
                            <Tooltip title="รหัสนี้ถูกใช้แล้ว">
                              <CancelIcon color="error" fontSize="small" />
                            </Tooltip>
                          )}
                        </InputAdornment>
                      ) : null
                    }}
                    error={codeCheckStatus === 'duplicate'}
                    helperText={
                      codeCheckStatus === 'duplicate' ? 'รหัสนี้ถูกใช้แล้ว กรุณาเปลี่ยนรหัสใหม่' :
                      codeCheckStatus === 'available' ? 'รหัสนี้ใช้ได้' :
                      'A-Z, 0-9 สูงสุด 6 ตัว'
                    }
                    FormHelperTextProps={{ sx: { color: codeCheckStatus === 'available' ? 'success.main' : undefined } }}
                    sx={{ '& .MuiInputBase-root': { height: 48 } }}
                  />
                  <Button variant="outlined" color="warning" onClick={handleGenerateImage} fullWidth sx={{ mt: 1.5 }}>
                    สร้างรูปภาพตัวอย่างคูปอง
                  </Button>
                  <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    <canvas ref={canvasRef} style={{ borderRadius: 8, maxWidth: '100%', boxShadow: imageGenerated ? '0 2px 10px rgba(0,0,0,0.15)' : 'none', display: imageGenerated ? 'block' : 'none' }} />
                    {imageGenerated && <Typography variant="caption" color="text.secondary">ตัวอย่างรูปภาพคูปอง (ไม่บันทึกในระบบ)</Typography>}
                  </Box>
                </Box>
              </>
            )}

            {/* Terms — hidden in edit mode */}
            {!editingCoupon && (
              <>
                <Divider />
                <Box>
                  <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.75, display: 'block' }}>เงื่อนไข</Typography>
                  <TextField label={t.termsTh} value={form.terms_th} onChange={e => setForm(f => ({ ...f, terms_th: e.target.value }))} fullWidth multiline rows={3} />
                </Box>
              </>
            )}

          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setDialogOpen(false)} variant="outlined" color="inherit">{t.cancel}</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleSave}
            disabled={saving || !form.business_id || !form.title || !form.discount_value || !form.valid_from || !form.valid_until}
            sx={{ minWidth: 100 }}
          >
            {saving ? <CircularProgress size={20} /> : t.save}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Business Edit Dialog */}
      <Dialog open={bizDialogOpen} onClose={() => setBizDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ pb: 1, fontWeight: 700, fontSize: '1.2rem' }}>แก้ไขข้อมูลธุรกิจ</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              label="ชื่อธุรกิจ"
              value={bizForm.business_name}
              onChange={e => setBizForm(f => ({ ...f, business_name: e.target.value }))}
              fullWidth
              sx={{ '& .MuiInputBase-root': { height: 48 } }}
            />
            <TextField
              label="เบอร์โทรศัพท์"
              value={bizForm.contact_phone}
              onChange={e => setBizForm(f => ({ ...f, contact_phone: e.target.value }))}
              fullWidth
              sx={{ '& .MuiInputBase-root': { height: 48 } }}
            />
            <TextField
              label="อีเมล"
              value={bizForm.contact_email}
              onChange={e => setBizForm(f => ({ ...f, contact_email: e.target.value }))}
              fullWidth
              type="email"
              sx={{ '& .MuiInputBase-root': { height: 48 } }}
            />
            <TextField
              label="ที่อยู่"
              value={bizForm.address}
              onChange={e => setBizForm(f => ({ ...f, address: e.target.value }))}
              fullWidth
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setBizDialogOpen(false)} variant="outlined" color="inherit">ยกเลิก</Button>
          <Button variant="contained" color="warning" onClick={handleSaveBiz} disabled={savingBiz} sx={{ minWidth: 100 }}>
            {savingBiz ? <CircularProgress size={20} /> : 'บันทึก'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Claim Dialog */}
      <Dialog open={claimDialog.open} onClose={() => setClaimDialog({ open: false, business: null })} maxWidth="sm" fullWidth>
        <DialogTitle>ขอยืนยันสิทธิ์ความเป็นเจ้าของ</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" mb={2}>
              สถานที่: <strong>{claimDialog.business?.venue_name || claimDialog.business?.business_name}</strong>
            </Typography>
            <Typography variant="body2" mb={2}>
              แนบเอกสารยืนยัน เช่น ใบแจ้งค่าไฟ, หนังสือรับรองบริษัท, สัญญาเช่า (JPG/PNG/PDF ไม่เกิน 5MB)
            </Typography>
            <Button
              component="label"
              variant="outlined"
              startIcon={<AttachFileIcon />}
              disabled={claimUploading}
              size="small"
            >
              {claimUploading ? 'กำลังอัปโหลด...' : 'เลือกไฟล์'}
              <input type="file" hidden accept="image/jpeg,image/png,image/webp,application/pdf" multiple onChange={handleClaimFileChange} />
            </Button>
            {claimDocs.length > 0 && (
              <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {claimDocs.map((doc, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, background: '#f8f9fa', p: 1, borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {doc.name || `เอกสาร ${i+1}`}
                    </Typography>
                    <IconButton size="small" onClick={() => setClaimDocs(prev => prev.filter((_, j) => j !== i))}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClaimDialog({ open: false, business: null })}>ยกเลิก</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleSubmitClaim}
            disabled={claimSubmitting || claimDocs.length === 0}
          >
            {claimSubmitting ? <CircularProgress size={20} /> : 'ส่งคำขอ'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Shop QR Code Dialog */}
      <Dialog open={!!shopQrBiz} onClose={() => setShopQrBiz(null)} maxWidth="xs" fullWidth>
        <DialogTitle>QR Code ร้านค้า</DialogTitle>
        <DialogContent>
          {shopQrBiz && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="body2" color="text.secondary" mb={2}>
                พิมพ์และแสดง QR Code นี้ที่ร้าน ให้ลูกค้าสแกนเพื่อใช้คูปองส่วนลด
              </Typography>
              <Box sx={{ display: 'inline-block', p: 2, bgcolor: '#fff', border: '2px solid #f39c12', borderRadius: 2 }}>
                <QRCode
                  value={JSON.stringify({ tvent: 'shop', token: shopQrBiz.shop_token })}
                  size={200}
                />
              </Box>
              <Typography variant="caption" display="block" color="text.secondary" mt={1.5}>
                {shopQrBiz.business_name || shopQrBiz.venue_name}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShopQrBiz(null)}>ปิด</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => window.print()}
          >
            พิมพ์ QR
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Container>
  );
};

export default VenueOwnerDashboard;
